import { Card, Select } from 'antd';
import React, { useState } from 'react';

const { Option } = Select;

function PrivacyPolicy() {
    const [language, setLanguage] = useState('en');

    const content = {
        en: {
            title: 'Privacy Policy',
            lastUpdated: 'Last Updated: December 16, 2024',
            sections: [
                {
                    title: '1. Introduction',
                    content:
                        'ReEvent ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our event management platform.',
                },
                {
                    title: '2. Information We Collect',
                    content:
                        'We collect information that you provide directly to us, including: (a) Account information: name, email address, and password; (b) Event data: events you create, participant information, attendance records; (c) Payment information: processed securely through Paddle (we do not store credit card details); (d) Usage data: how you interact with our Service, including IP address, browser type, and device information.',
                },
                {
                    title: '3. How We Use Your Information',
                    content:
                        'We use the information we collect to: (a) Provide, maintain, and improve our Service; (b) Process your transactions and manage your subscription; (c) Send you technical notices, updates, and support messages; (d) Respond to your comments and questions; (e) Monitor and analyze trends and usage; (f) Detect and prevent fraud and abuse.',
                },
                {
                    title: '4. Information Sharing',
                    content:
                        'We do not sell your personal information. We may share your information with: (a) Service providers: Paddle for payment processing, hosting providers for data storage; (b) Legal requirements: when required by law or to protect our rights; (c) Business transfers: in connection with a merger, sale, or acquisition.',
                },
                {
                    title: '5. Data Security',
                    content:
                        'We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure. We use HTTPS encryption, secure password hashing, and regular security audits.',
                },
                {
                    title: '6. Data Retention',
                    content:
                        'We retain your personal information for as long as your account is active or as needed to provide you services. You may request deletion of your account at any time through the Settings page. Some information may be retained for legal or legitimate business purposes.',
                },
                {
                    title: '7. Your Rights',
                    content:
                        'You have the right to: (a) Access your personal information; (b) Correct inaccurate data; (c) Request deletion of your data; (d) Object to processing of your data; (e) Export your data; (f) Withdraw consent at any time. Contact us at support@reevent.io to exercise these rights.',
                },
                {
                    title: '8. Cookies and Tracking',
                    content:
                        'We use cookies and similar tracking technologies to track activity on our Service. We use session cookies for authentication and functional cookies to remember your preferences. You can instruct your browser to refuse cookies, but some features may not function properly.',
                },
                {
                    title: '9. Third-Party Services',
                    content:
                        'Our Service uses third-party services including Paddle (payment processing) and Google Analytics (usage analytics). These services have their own privacy policies governing their use of your information.',
                },
                {
                    title: '10. Children\'s Privacy',
                    content:
                        'Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.',
                },
                {
                    title: '11. International Data Transfers',
                    content:
                        'Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.',
                },
                {
                    title: '12. Changes to Privacy Policy',
                    content:
                        'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Significant changes will be communicated via email.',
                },
                {
                    title: '13. Contact Us',
                    content:
                        'If you have any questions about this Privacy Policy, please contact us at: Email: support@reevent.io',
                },
            ],
        },
        is: {
            title: 'Persónuverndarstefna',
            lastUpdated: 'Síðast uppfært: 16. desember 2024',
            sections: [
                {
                    title: '1. Inngangur',
                    content:
                        'ReEvent ("við", "okkar" eða "okkur") er skuldbundið til að vernda friðhelgi þína. Þessi persónuverndarstefna útskýrir hvernig við söfnum, notum, birtum og verndum upplýsingar þínar þegar þú notar viðburðastjórnunarvettvang okkar.',
                },
                {
                    title: '2. Upplýsingar sem við söfnum',
                    content:
                        'Við söfnum upplýsingum sem þú veitir okkur beint, þar á meðal: (a) Reikningsupplýsingar: nafn, netfang og lykilorð; (b) Viðburðagögn: viðburðir sem þú býrð til, upplýsingar um þátttakendur, mætingarskrár; (c) Greiðsluupplýsingar: unnar á öruggan hátt í gegnum Paddle (við geymum ekki kreditkortaupplýsingar); (d) Notkunargögn: hvernig þú hefur samskipti við þjónustu okkar, þar á meðal IP-tölu, vafraupplýsingar og tækjaupplýsingar.',
                },
                {
                    title: '3. Hvernig við notum upplýsingar þínar',
                    content:
                        'Við notum upplýsingarnar sem við söfnum til að: (a) Veita, viðhalda og bæta þjónustu okkar; (b) Vinna úr viðskiptum þínum og stjórna áskrift þinni; (c) Senda þér tæknilegar tilkynningar, uppfærslur og stuðningsskilaboð; (d) Svara athugasemdum og spurningum þínum; (e) Fylgjast með og greina þróun og notkun; (f) Greina og koma í veg fyrir svik og misnotkun.',
                },
                {
                    title: '4. Miðlun upplýsinga',
                    content:
                        'Við seljum ekki persónuupplýsingar þínar. Við gætum deilt upplýsingum þínum með: (a) Þjónustuveitendum: Paddle fyrir greiðsluvinnslu, hýsingaraðilum fyrir gagnageymslu; (b) Lagalegum kröfum: þegar lög krefjast eða til að vernda réttindi okkar; (c) Viðskiptatilfærslum: í tengslum við samruna, sölu eða yfirtöku.',
                },
                {
                    title: '5. Gagnaöryggi',
                    content:
                        'Við innleiðum viðeigandi tæknilegar og skipulagslegar ráðstafanir til að vernda persónuupplýsingar þínar. Hins vegar er engin aðferð til að senda gögn yfir internetið 100% örugg. Við notum HTTPS dulkóðun, örugga lykilorðakóðun og reglulegar öryggisúttektir.',
                },
                {
                    title: '6. Gagnageymsla',
                    content:
                        'Við geymum persónuupplýsingar þínar svo lengi sem reikningurinn þinn er virkur eða eftir þörfum til að veita þér þjónustu. Þú getur óskað eftir eyðingu reikningsins þíns hvenær sem er í gegnum stillingasíðuna. Sumar upplýsingar geta verið geymdar í löglegum eða lögmætum viðskiptalegum tilgangi.',
                },
                {
                    title: '7. Réttindi þín',
                    content:
                        'Þú hefur rétt til að: (a) Fá aðgang að persónuupplýsingum þínum; (b) Leiðrétta rangar upplýsingar; (c) Óska eftir eyðingu gagna þinna; (d) Andmæla vinnslu gagna þinna; (e) Flytja út gögnin þín; (f) Afturkalla samþykki hvenær sem er. Hafðu samband við okkur á support@reevent.io til að nýta þessi réttindi.',
                },
                {
                    title: '8. Vafrakökur og eftirlit',
                    content:
                        'Við notum vafrakökur og svipaða rakningartækni til að fylgjast með virkni á þjónustu okkar. Við notum lotuvafrakökur fyrir auðkenningu og virknisvafrakökur til að muna kjörstillingar þínar. Þú getur gefið vafranum þínum fyrirmæli um að hafna vafrakökum, en sumir eiginleikar virka hugsanlega ekki rétt.',
                },
                {
                    title: '9. Þjónusta þriðja aðila',
                    content:
                        'Þjónusta okkar notar þjónustu þriðja aðila þar á meðal Paddle (greiðsluvinnsla) og Google Analytics (notkunargreiningar). Þessi þjónusta hefur sínar eigin persónuverndarstefnur sem stjórna notkun þeirra á upplýsingum þínum.',
                },
                {
                    title: '10. Friðhelgi barna',
                    content:
                        'Þjónusta okkar er ekki ætluð börnum yngri en 13 ára. Við söfnum ekki vísvitandi persónuupplýsingum frá börnum yngri en 13 ára. Ef þú verður var við að barn hafi veitt okkur persónuupplýsingar skaltu hafa samband við okkur.',
                },
                {
                    title: '11. Alþjóðleg gagnaflutningur',
                    content:
                        'Upplýsingar þínar geta verið fluttar til og unnar í öðrum löndum en búsetulandi þínu. Við tryggum að viðeigandi verndarráðstafanir séu til staðar til að vernda upplýsingar þínar í samræmi við þessa persónuverndarstefnu.',
                },
                {
                    title: '12. Breytingar á persónuverndarstefnu',
                    content:
                        'Við kunnum að uppfæra þessa persónuverndarstefnu af og til. Við munum tilkynna þér um allar breytingar með því að birta nýju persónuverndarstefnuna á þessari síðu og uppfæra dagsetninguna "Síðast uppfært". Verulegar breytingar verða sendar með tölvupósti.',
                },
                {
                    title: '13. Hafðu samband við okkur',
                    content:
                        'Ef þú hefur spurningar um þessa persónuverndarstefnu skaltu hafa samband við okkur á: Netfang: support@reevent.io',
                },
            ],
        },
    };

    const currentContent = content[language];

    return (
        <div className="container main">
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px',
                    }}
                >
                    <h1>{currentContent.title}</h1>
                    <Select
                        value={language}
                        onChange={setLanguage}
                        style={{ width: 150 }}
                    >
                        <Option value="en">English</Option>
                        <Option value="is">Íslenska</Option>
                    </Select>
                </div>

                <Card>
                    <p style={{ color: '#666', marginBottom: '24px' }}>
                        {currentContent.lastUpdated}
                    </p>

                    {currentContent.sections.map((section, index) => (
                        <div key={index} style={{ marginBottom: '24px' }}>
                            <h3>{section.title}</h3>
                            <p style={{ lineHeight: '1.6' }}>{section.content}</p>
                        </div>
                    ))}
                </Card>
            </div>
        </div>
    );
}

export default PrivacyPolicy;

